import {FilterCategory, FilterSets} from "@/components/filterDropdown/FilterDropdown.ts";
import {Sorting} from "@/components/table/Table.types.ts";
import {useDebouncedSearch, UseDebouncedSearchOptions} from "@/lib/hooks/useDebouncedSearch.ts";
import {useFilterSets, UseFilterSetsOptions} from "@/lib/hooks/useFilterSets.ts";
import {usePagination, UsePaginationOptions} from "@/lib/hooks/usePagination.ts";
import {useSorting, UseSortingOptions} from "@/lib/hooks/useSorting.ts";

interface UseTableGlobalOptions {
  resetPaginationOnSearch?: boolean;
  resetPaginationOnFilter?: boolean;
  resetPaginationOnSort?: boolean;
}

interface UseTableOptions
  extends
    UseDebouncedSearchOptions,
    UsePaginationOptions,
    UseSortingOptions,
    UseFilterSetsOptions,
    UseTableGlobalOptions {}

export const useTable = ({
  searchKey,
  debounceDelay,
  onSearchChange,
  clearKeysOnSearchChange,
  offsetKey,
  limitKey,
  defaultLimit,
  defaultOffset,
  onPaginationChange,
  sortKey,
  defaultSort,
  onSortChange,
  filtersKey,
  sessionStorageKey,
  onFilterSetChange,
  useUrlParams = true,
  resetPaginationOnSearch = true,
  resetPaginationOnFilter = true,
  resetPaginationOnSort = false,
}: UseTableOptions = {}) => {
  const handleSearchChange = (newSearch: string, params: URLSearchParams) => {
    if (resetPaginationOnSearch && pagination.pagination.offset !== 0) {
      pagination.setPagination({...pagination.pagination, offset: 0}, params);
    }
    onSearchChange?.(newSearch, params);
  };

  const handleSortChange = (newSorting: Sorting, params: URLSearchParams) => {
    if (resetPaginationOnSort && pagination.pagination.offset !== 0) {
      pagination.setPagination({...pagination.pagination, offset: 0}, params);
    }
    onSortChange?.(newSorting, params);
  };

  const handleFilterSetChange = (newFilterSets: FilterSets<FilterCategory>, params: URLSearchParams) => {
    if (resetPaginationOnFilter && pagination.pagination.offset !== 0) {
      pagination.setPagination({...pagination.pagination, offset: 0}, params);
    }
    onFilterSetChange?.(newFilterSets, params);
  };

  const search = useDebouncedSearch({
    searchKey,
    debounceDelay,
    onSearchChange: handleSearchChange,
    useUrlParams,
    clearKeysOnSearchChange,
  });

  const pagination = usePagination({
    offsetKey,
    limitKey,
    defaultLimit,
    defaultOffset,
    useUrlParams,
    onPaginationChange,
  });

  const sorting = useSorting({
    sortKey,
    defaultSort,
    useUrlParams,
    onSortChange: handleSortChange,
  });

  const filters = useFilterSets({
    filtersKey,
    sessionStorageKey,
    onFilterSetChange: handleFilterSetChange,
    useUrlParams,
  });

  // Until restored filters reach the URL, the list already asks for page 1, where their restore moves it.
  const shownPagination =
    resetPaginationOnFilter && filters.isRestoringStoredFilterSets
      ? {...pagination.pagination, offset: 0}
      : pagination.pagination;

  return {
    search: search.search,
    searchDebounced: search.searchDebounced,
    searchChanged: search.searchChanged,
    discardPendingSearch: search.discardPendingSearch,
    setSearch: search.setSearch,
    setSearchDebounced: search.setSearchDebounced,
    pagination: shownPagination,
    setPagination: pagination.setPagination,
    sorting: sorting.sorting,
    setSorting: sorting.setSorting,
    sortingString: sorting.sortingString,
    filterSets: filters.filterSets,
    setFilterSets: filters.setFilterSets,
  };
};
